import {useMemo} from "react";
import {dibAdminApi} from "../../dibAdminApi/dibAdminApi";
import {usePortalData} from "./usePortalData";
import {groupingSlug} from "./PortalsPage";
import {useNewsFilters} from "./useNewsFilters";

export type NewsGroupingOption = {
    slug: string;
    name: string;
    dbIds: number[];
    /** No news from the selected source touch this grouping's products. */
    disabled: boolean;
};

/**
 * Portal groupings as the news area filter: the same relevance-filtered
 * groupings the portals tab shows, narrowed to those whose products have at
 * least one news article (per v2/databaseIds). Selecting a grouping (its slug
 * in the URL, see useNewsFilters) filters news by that grouping's product
 * database ids.
 *
 * With a source selected, the same endpoint is asked again for that source
 * alone; groupings outside the answer are marked `disabled` rather than
 * dropped, so the offered set stays stable while the source changes.
 */
export function useNewsGroupings() {
    const {relevantGroupings, isLoading: portalLoading} = usePortalData();
    const {
        grouping: selectedSlug,
        source,
        setValue,
        toggleValue,
    } = useNewsFilters();
    const {data: newsDbIds, isLoading: idsLoading} =
        dibAdminApi.endpoints.getNewsDatabaseIds.useQuery({});
    // currentData, not data: it is undefined while a newly picked source is
    // still loading, so one source's answer never dims areas for another.
    const {currentData: sourceDbIds} =
        dibAdminApi.endpoints.getNewsDatabaseIds.useQuery(
            {sources: source ? [source] : []},
            {skip: !source}
        );

    const options = useMemo<NewsGroupingOption[]>(() => {
        const withNews = new Set(newsDbIds ?? []);
        // null while no source is picked, and while its ids are still in
        // flight — so rows don't flash disabled before the answer arrives.
        // The `source` guard is load-bearing: a skipped query keeps serving
        // its last result, which would leave areas dimmed after the source
        // is cleared.
        const withSourceNews =
            source && sourceDbIds ? new Set(sourceDbIds) : null;
        return relevantGroupings.flatMap((g) => {
            if (!g.name || !g.products_json) return [];
            const dbIds = (JSON.parse(g.products_json) as string[]).map(Number);
            if (!dbIds.some((id) => withNews.has(id))) return [];
            return [
                {
                    slug: groupingSlug(g.name, g.id),
                    name: g.name,
                    dbIds,
                    disabled: withSourceNews
                        ? !dbIds.some((id) => withSourceNews.has(id))
                        : false,
                },
            ];
        });
    }, [relevantGroupings, newsDbIds, source, sourceDbIds]);

    // The selected grouping's product ids, ascending — sorted so the news
    // query's cache key is stable. A stale slug (e.g. an old link) is
    // silently ignored. A disabled grouping still contributes its ids; the
    // news list then legitimately comes back empty.
    const databaseIds = useMemo(() => {
        const selected = options.find((o) => o.slug === selectedSlug);
        return [...(selected?.dbIds ?? [])].sort((a, b) => a - b);
    }, [options, selectedSlug]);

    return {
        options,
        selectedSlug,
        toggle: (slug: string) => toggleValue("groupings", slug),
        clear: () => setValue("groupings", null),
        databaseIds,
        isLoading: portalLoading || idsLoading,
    };
}
